"use client";

import { Button } from "@/components/atoms/buttons";
import ConfirmDelete from "@/components/molecules/confirm-delete";
import PromoCodeForm from "@/components/organisms/forms/checkout/promo-code-form";
import OrderSummaryFooter from "@/components/templates/Checkout/components/order-summary-footer";
import ProductSummaryCard from "@/components/templates/Checkout/components/product-summary-card";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Pagination from "@/components/ui/pagination";
import { ITEMS_PER_PAGE, useCartProducts } from "@/hooks/useCartProducts";
import { Loader2, Trash2 } from "lucide-react";
import { isEventInfoRoute } from "@/components/templates/Checkout";

type PaymentSidebarProps = {
  variant?: "default" | "secondary";
  className?: string;
  showPromoInput?: boolean;
  locale: Locales;
  route: string;
};

const PaymentSidebar = ({
  variant = "secondary",
  className,
  showPromoInput = false,
  locale,
  route,
}: PaymentSidebarProps) => {
  const {
    itemCount,
    isModalOpen,
    setIsModalOpen,
    handleClearCart,
    isClearingCart,
    shouldShowLoader,
    currentPageItems,
    currentPage,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
    goToNextPage,
    goToPrevPage,
    goToPage,
  } = useCartProducts(locale);

  if (shouldShowLoader) {
    return (
      <aside className="flex flex-2/5 items-center justify-center lg:max-w-[531px] lg:min-w-[280px]">
        <Loader2 className="animate-spin" />
      </aside>
    );
  }

  return (
    <aside className={cn("flex-2/5 lg:min-w-[480px] xl:max-w-[531px]", className)}>
      <header className="flex items-center justify-between">
        <h2
          className={cn(
            "font-cabinet-grotesk leading-h1 text-3xl font-medium max-sm:hidden",
            variant === "secondary" && "hidden"
          )}
        >
          {__("In your cart", locale)}
        </h2>
        <h2
          className={cn(
            "leading-h1 font-cabinet-grotesk text-2xl font-medium md:text-3xl",
            variant === "default" && "text-lg min-sm:hidden"
          )}
        >
          {__("Order Summary", locale)}
        </h2>
        {variant === "default" && (
          <div className="flex items-center gap-2.5 max-sm:hidden">
            {itemCount >= 1 && (
              <ConfirmDelete
                locale={locale}
                onClick={handleClearCart}
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                isActionHappening={isClearingCart}
                modalTrigger={
                  <Button
                    variant="secondary"
                    className="border-primary-light/50 h-11 bg-inherit"
                    size="md"
                    disabled={isClearingCart}
                  >
                    <Trash2 />
                    {__("Empty", locale)}
                  </Button>
                }
                title={__("Delete cart", locale)}
                text={__("Are you sure you want to delete the cart?", locale)}
              />
            )}
            <Link href={getPermalink(locale, "cart")} className="text-xl font-medium underline underline-offset-2">
              {__("Edit", locale)}
            </Link>
          </div>
        )}
      </header>
      <hr className={cn("border-primary-light/50 max-sm:hidden sm:mt-5", variant === "default" && "hidden")} />
      <ul className="max-sm:border-primary-light/50 mt-4 space-y-3 max-sm:py-5 sm:mt-5">
        {currentPageItems.map(({ product, prismicProduct, cartItem }) => (
          <ProductSummaryCard
            locale={locale}
            key={cartItem.id}
            variant={isEventInfoRoute(locale, route) ? "default" : "secondary"}
            prismicProduct={prismicProduct}
            medusaProduct={product}
            cartItem={cartItem}
          />
        ))}
      </ul>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          goToPrevPage={goToPrevPage}
          goToNextPage={goToNextPage}
          startIndex={startIndex}
          endIndex={endIndex}
          locale={locale}
          goToPage={goToPage}
          totalPages={totalPages}
          showPaginationLabels={false}
        />
      )}
      <OrderSummaryFooter locale={locale} className="mt-5 space-y-5" />
      <PromoCodeForm locale={locale} className={cn("mt-5 hidden", showPromoInput && "block sm:hidden")} />
    </aside>
  );
};

export default PaymentSidebar;
